<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import MessageApi from 'dashboard/api/inbox/message';
import ConversationApi from 'dashboard/api/inbox/conversation';
import CrmAPI from 'dashboard/api/crm';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TemplatesPicker from 'dashboard/components/widgets/conversation/WhatsappTemplates/TemplatesPicker.vue';
import CannedResponse from 'dashboard/components/widgets/conversation/CannedResponse.vue';
import WhatsAppTemplateReply from 'dashboard/components/widgets/conversation/WhatsappTemplates/WhatsAppTemplateReply.vue';
import EmojiPicker from 'shared/components/emoji/EmojiPicker.vue';
import PatientSpaceIcon from 'dashboard/routes/dashboard/patient/PatientSpaceIcon.vue';
import ChatQuickActions from './ChatQuickActions.vue';
import { onClickOutside } from '@vueuse/core';

const props = defineProps({
  contact: { type: Object, required: true }, // card do CRM (contact_json)
  pipelineId: { type: Number, default: null },
  stages: { type: Array, default: () => [] }, // colunas do funil (mover card)
});

const emit = defineEmits(['close', 'replied', 'resolved', 'conversationStarted']);

const { t } = useI18n();
const store = useStore();
const { isAdmin } = useAdmin();
const route = useRoute();
const router = useRouter();

// Espaço do Paciente: página única com toda a jornada do paciente
const openPatientSpace = () => {
  emit('close');
  router.push(frontendURL(`accounts/${route.params.accountId}/patient/${props.contact.contact_id}`));
};

const conversationId = computed(() => props.contact.last_conversation_id);
const hasConversation = computed(() => !!conversationId.value);
const inboxId = computed(() => props.contact.last_conversation?.inbox_id);
const isWhatsapp = computed(
  () => props.contact.last_conversation?.channel_type === 'Channel::Whatsapp'
);

// ── Iniciar conversa (cards criados à mão, sem conversa ainda) ──
const inboxes = useMapGetter('inboxes/getInboxes');
const whatsappInboxes = computed(() =>
  inboxes.value.filter(i => i.channel_type === 'Channel::Whatsapp')
);
const startInboxId = ref(null);
const isStarting = ref(false);

// pré-seleção: caixa PRINCIPAL da coluna do card (config da coluna) > 1ª caixa
const stageMainInboxIds = computed(() => {
  const stage = (props.stages || []).find(s => s.id === props.contact.stage_id);
  return stage?.main_inbox_ids || [];
});

watch(whatsappInboxes, list => {
  if (startInboxId.value || !list.length) return;
  const main = list.find(i => stageMainInboxIds.value.includes(i.id));
  startInboxId.value = (main || list[0]).id;
}, { immediate: true });

const startConversation = async () => {
  if (!startInboxId.value || isStarting.value) return;
  isStarting.value = true;
  try {
    const data = await store.dispatch('crm/startConversation', {
      pipelineId: props.pipelineId,
      id: props.contact.id,
      inboxId: startInboxId.value,
    });
    emit('conversationStarted', data);
  } catch (error) {
    useAlert(error?.response?.data?.error || t('CRM.ERROR.GENERIC'));
  } finally {
    isStarting.value = false;
  }
};

const messages = ref([]);
const isLoading = ref(true);
const isLoadingMore = ref(false);
const hasMore = ref(false);
const isSending = ref(false);
const replyText = ref('');
const messagesEl = ref(null);

// status da conversa (open/resolved/pending/snoozed)
const convStatus = ref(props.contact.last_conversation?.status || 'open');
const isResolving = ref(false);

// templates (remarketing)
const showTemplates = ref(false);
const selectedTemplate = ref(null);

// emoji picker
const showEmoji = ref(false);
const emojiWrap = ref(null);
onClickOutside(emojiWrap, () => { showEmoji.value = false; });
const onInsertEmoji = e => {
  replyText.value += e.emoji ?? e.value ?? '';
};

// message_type: 0 incoming | 1 outgoing | 2 activity | 3 template
const isIncoming = m => m.message_type === 0;
const isActivity = m => m.message_type === 2;

const toDate = ts =>
  typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);

const formatTime = ts => {
  const d = toDate(ts);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return time;
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${time}`;
};

const attachmentLabel = fileType => {
  const map = {
    image: '🖼️ Imagem',
    audio: '🎧 Áudio',
    video: '🎬 Vídeo',
    file: '📎 Arquivo',
    location: '📍 Localização',
    contact: '👤 Contato',
  };
  return map[fileType] ?? '📎 Anexo';
};

const scrollToBottom = async () => {
  await nextTick();
  const el = messagesEl.value;
  if (!el) return;
  el.scrollTop = el.scrollHeight;
  // garante o fundo mesmo com anexos/imagens que renderizam depois
  setTimeout(() => { el.scrollTop = el.scrollHeight; }, 120);
  setTimeout(() => { el.scrollTop = el.scrollHeight; }, 400);
};

const loadMessages = async () => {
  isLoading.value = true;
  try {
    const { data } = await MessageApi.getPreviousMessages({
      conversationId: conversationId.value,
    });
    const payload = data.payload ?? [];
    messages.value = payload.filter(m => !m.private);
    hasMore.value = payload.length >= 20;
    await scrollToBottom();
    // marca como lida (zera "não lidas" na caixa de entrada e no card)
    ConversationApi.markMessageRead({ id: conversationId.value }).catch(() => {});
  } catch {
    useAlert(t('CRM.ERROR.GENERIC'));
  } finally {
    isLoading.value = false;
  }
};

const loadMore = async () => {
  if (isLoadingMore.value || !messages.value.length) return;
  isLoadingMore.value = true;
  try {
    const { data } = await MessageApi.getPreviousMessages({
      conversationId: conversationId.value,
      before: messages.value[0].id,
    });
    const payload = (data.payload ?? []).filter(m => !m.private);
    messages.value = [...payload, ...messages.value];
    hasMore.value = payload.length >= 20;
  } catch {
    useAlert(t('CRM.ERROR.GENERIC'));
  } finally {
    isLoadingMore.value = false;
  }
};

// ── Chat vivo: busca mensagens novas a cada 4s enquanto o balão está aberto ──
let pollTimer = null;

const pollNewMessages = async () => {
  if (!conversationId.value || isLoading.value) return;
  const lastId = messages.value[messages.value.length - 1]?.id;
  if (!lastId) return;
  try {
    const { data } = await MessageApi.getPreviousMessages({
      conversationId: conversationId.value,
      after: lastId,
    });
    const incoming = (data.payload ?? []).filter(
      m => !m.private && m.id > lastId && !messages.value.some(x => x.id === m.id)
    );
    if (incoming.length) {
      messages.value.push(...incoming);
      await scrollToBottom();
      // paciente respondeu → some o "aguardando resposta" e marca como lida
      ConversationApi.markMessageRead({ id: conversationId.value }).catch(() => {});
      emit('replied');
    }
  } catch { /* silencioso — tenta de novo no próximo tick */ }
};

const stopPolling = () => {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
};
const startPolling = () => {
  stopPolling();
  pollTimer = setInterval(pollNewMessages, 4000);
};

onBeforeUnmount(stopPolling);

// ── Painel do robô de follow-up (etiquetas e coluna agora ficam na
// barra de ações rápidas, sempre visível abaixo do cabeçalho) ──
const showCardPanel = ref(false);

// ── Trava do follow-up: pausa por PACIENTE + chave por ROBÔ ──
// A atendente para o robô sem depender de admin: ou silencia só este
// paciente, ou desliga o robô inteiro (chave de emergência).
const followup = ref(null); // { paused, paused_by, bots: [{id, name, active}] }
const isTogglingFollowup = ref(false);

const loadFollowup = async () => {
  if (!conversationId.value) return;
  try {
    const { data } = await CrmAPI.getConversationSummary(conversationId.value);
    followup.value = data.followup || null;
  } catch { /* sem o bloco — painel segue funcionando */ }
};
watch(showCardPanel, open => {
  if (open && !followup.value) loadFollowup();
});

const togglePatientPause = async () => {
  if (isTogglingFollowup.value || !followup.value || !conversationId.value) return;
  isTogglingFollowup.value = true;
  try {
    const { data } = await CrmAPI.toggleFollowupPause(conversationId.value, !followup.value.paused);
    followup.value = data.followup;
    useAlert(followup.value.paused
      ? t('CRM.CHAT.FOLLOWUP_PAUSED_PATIENT')
      : t('CRM.CHAT.FOLLOWUP_RESUMED_PATIENT'));
  } catch {
    useAlert(t('CRM.ERROR.GENERIC'));
  } finally {
    isTogglingFollowup.value = false;
  }
};

const toggleFollowupBot = async bot => {
  if (isTogglingFollowup.value) return;
  isTogglingFollowup.value = true;
  try {
    const { data } = await CrmAPI.toggleFollowupBot(bot.id);
    bot.active = data.active;
    useAlert(data.active
      ? t('CRM.CHAT.FOLLOWUP_BOT_ON', { name: bot.name })
      : t('CRM.CHAT.FOLLOWUP_BOT_OFF', { name: bot.name }));
  } catch {
    useAlert(t('CRM.ERROR.GENERIC'));
  } finally {
    isTogglingFollowup.value = false;
  }
};

// telefone copiável ao lado do nome
const copyPhone = async () => {
  if (!props.contact.phone_number) return;
  try {
    await navigator.clipboard.writeText(props.contact.phone_number);
    useAlert(t('CRM.CHAT.PHONE_COPIED'));
  } catch { /* clipboard bloqueado — o texto continua selecionável */ }
};

const sendReply = async () => {
  const content = replyText.value.trim();
  if (!content || isSending.value) return;
  isSending.value = true;
  try {
    const { data } = await MessageApi.create({
      conversationId: conversationId.value,
      message: content,
      private: false,
    });
    messages.value.push(data);
    replyText.value = '';
    await scrollToBottom();
    emit('replied');
  } catch {
    useAlert(t('CRM.CHAT.SEND_ERROR'));
  } finally {
    isSending.value = false;
  }
};

// mensagens rápidas: texto começando com "/" abre a lista (como na
// caixa de resposta nativa); Enter/clique substitui o texto pelo conteúdo
const cannedResponses = useMapGetter('getCannedResponses');
const showCannedMenu = computed(() => replyText.value.startsWith('/'));
const cannedSearchKey = computed(() =>
  showCannedMenu.value ? replyText.value.slice(1) : ''
);
const replaceTextWithCanned = message => {
  replyText.value = message;
};

const onKeydown = e => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    // com a lista de mensagens rápidas aberta, Enter escolhe o item
    // (o MentionBox escuta o teclado no documento)
    if (showCannedMenu.value && cannedResponses.value.length) return;
    sendReply();
  }
};

// ── Resolver / reabrir conversa ────────────────────────────
const isResolved = computed(() => convStatus.value === 'resolved');

const toggleResolve = async () => {
  if (isResolving.value) return;
  isResolving.value = true;
  const nextStatus = isResolved.value ? 'open' : 'resolved';
  try {
    await ConversationApi.toggleStatus({
      conversationId: conversationId.value,
      status: nextStatus,
    });
    convStatus.value = nextStatus;
    useAlert(
      nextStatus === 'resolved' ? t('CRM.CHAT.RESOLVED') : t('CRM.CHAT.REOPENED')
    );
    emit('resolved', { conversationId: conversationId.value, status: nextStatus });
  } catch {
    useAlert(t('CRM.ERROR.GENERIC'));
  } finally {
    isResolving.value = false;
  }
};

// ── Templates (remarketing) ────────────────────────────────
const openTemplates = () => {
  selectedTemplate.value = null;
  showTemplates.value = true;
  // garante que as inboxes/templates estão carregadas para o picker
  store.dispatch('inboxes/get').catch(() => {});
};

const onTemplateSelect = template => {
  selectedTemplate.value = template;
};

const onTemplateSend = async payload => {
  if (isSending.value) return;
  isSending.value = true;
  try {
    const { data } = await MessageApi.create({
      conversationId: conversationId.value,
      message: payload.message,
      templateParams: payload.templateParams,
      private: false,
    });
    messages.value.push(data);
    showTemplates.value = false;
    selectedTemplate.value = null;
    await scrollToBottom();
    // enviar template reabre a conversa no fluxo de atendimento
    if (isResolved.value) convStatus.value = 'open';
    emit('replied');
  } catch {
    useAlert(t('CRM.CHAT.SEND_ERROR'));
  } finally {
    isSending.value = false;
  }
};

onMounted(() => {
  if (hasConversation.value) {
    loadMessages();
    startPolling();
  } else {
    isLoading.value = false;
    if (!inboxes.value.length) store.dispatch('inboxes/get');
  }
});

// conversa criada agora há pouco → carrega o (vazio) histórico e segue o fluxo
watch(conversationId, id => {
  if (id) {
    convStatus.value = props.contact.last_conversation?.status || 'open';
    loadMessages();
    startPolling();
  }
});
</script>

<template>
  <div
    class="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
    @click.self="emit('close')"
  >
    <div class="bg-n-solid-1 rounded-2xl shadow-2xl w-full max-w-lg h-[85vh] flex flex-col">
      <!-- Header -->
      <div class="flex items-center gap-3 px-4 py-3 border-b border-n-weak flex-shrink-0">
        <div class="w-9 h-9 rounded-full bg-n-brand flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 overflow-hidden">
          <img v-if="contact.avatar_url" :src="contact.avatar_url" class="w-9 h-9 object-cover" />
          <span v-else>{{ contact.name?.[0]?.toUpperCase() ?? '?' }}</span>
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 min-w-0">
            <p class="text-sm font-semibold text-n-slate-12 truncate">{{ contact.name }}</p>
            <!-- telefone copiável ao lado do nome -->
            <button
              v-if="contact.phone_number"
              class="flex items-center gap-1 text-xs text-n-slate-10 hover:text-n-brand select-text flex-shrink-0"
              :title="$t('CRM.CHAT.COPY_PHONE')"
              @click="copyPhone"
            >
              {{ contact.phone_number }}
              <span class="i-lucide-copy text-[11px]" />
            </button>
          </div>
          <p class="text-xs text-n-slate-10 truncate">
            {{ contact.last_conversation?.inbox_name }} · #{{ conversationId }}
          </p>
          <!-- atribuição CTWA: por qual anúncio o lead chegou (visão de admin) -->
          <a
            v-if="isAdmin && contact.meta_ads"
            :href="contact.meta_ads.source_url || undefined"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center gap-1 text-[11px] text-blue-600 truncate"
            :class="contact.meta_ads.source_url ? 'hover:underline' : 'pointer-events-none'"
            :title="contact.meta_ads.ad_name || contact.meta_ads.headline || contact.meta_ads.source_id"
          >
            <span class="i-lucide-megaphone text-[11px] flex-shrink-0" />
            <span class="truncate">
              Anúncio: {{ contact.meta_ads.ad_name || contact.meta_ads.headline || contact.meta_ads.source_id }}
            </span>
          </a>
        </div>
        <!-- Status resolvido -->
        <span
          v-if="isResolved"
          class="text-[10px] font-medium px-2 py-0.5 rounded-full bg-green-500/15 text-green-600 flex-shrink-0"
        >
          {{ $t('CRM.CHAT.STATUS_RESOLVED') }}
        </span>
        <!-- Espaço do Paciente -->
        <button
          class="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 transition-transform hover:scale-110"
          title="Espaço do Paciente"
          @click="openPatientSpace"
        >
          <PatientSpaceIcon :size="22" />
        </button>
        <!-- Painel do robô de follow-up -->
        <button
          v-if="hasConversation"
          class="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 transition-colors"
          :class="showCardPanel ? 'bg-n-brand text-white' : 'text-n-slate-10 hover:text-n-brand hover:bg-n-brand/10'"
          :title="$t('CRM.CHAT.FOLLOWUP_TITLE')"
          @click="showCardPanel = !showCardPanel"
        >
          <span class="i-lucide-bot text-base" />
        </button>
        <button
          class="text-n-slate-10 hover:text-n-slate-12 i-lucide-x text-xl flex-shrink-0"
          @click="emit('close')"
        />
      </div>

      <!-- ── Barra de ações rápidas: etiquetas + mover de coluna ── -->
      <ChatQuickActions
        :contact="contact"
        :pipeline-id="pipelineId"
        :stages="stages"
      />

      <!-- ── Painel do robô de follow-up ── -->
      <div v-if="showCardPanel" class="px-4 py-3 border-b border-n-weak flex-shrink-0 space-y-2.5 bg-n-alpha-1">
        <p v-if="!followup" class="text-xs text-n-slate-10">
          {{ $t('CRM.MODAL.LOADING') }}
        </p>
        <p
          v-else-if="!followup.bots.length && !followup.paused"
          class="text-xs text-n-slate-10"
        >
          {{ $t('CRM.CHAT.FOLLOWUP_EMPTY') }}
        </p>

        <!-- 🤖 Trava do robô de follow-up -->
        <div v-if="followup && (followup.bots.length || followup.paused)">
          <label class="text-xs font-medium text-n-slate-11 block mb-1.5">
            🤖 {{ $t('CRM.CHAT.FOLLOWUP_TITLE') }}
          </label>
          <button
            class="w-full flex items-center justify-between gap-2 text-xs px-2.5 py-2 rounded-lg border transition-colors disabled:opacity-50"
            :class="followup.paused
              ? 'border-amber-400/60 bg-amber-400/10 text-amber-600 dark:text-amber-400 font-medium'
              : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'"
            :disabled="isTogglingFollowup"
            @click="togglePatientPause"
          >
            <span class="flex items-center gap-1.5">
              <span :class="followup.paused ? 'i-lucide-bell-off' : 'i-lucide-bell'" class="text-sm" />
              {{ followup.paused
                ? $t('CRM.CHAT.FOLLOWUP_PAUSED_LABEL', { by: followup.paused_by || '—' })
                : $t('CRM.CHAT.FOLLOWUP_PAUSE_PATIENT') }}
            </span>
            <span class="font-semibold flex-shrink-0">
              {{ followup.paused ? $t('CRM.CHAT.FOLLOWUP_RESUME') : $t('CRM.CHAT.FOLLOWUP_PAUSE') }}
            </span>
          </button>
          <div v-if="followup.bots.length" class="mt-1.5 space-y-1">
            <div
              v-for="bot in followup.bots"
              :key="bot.id"
              class="flex items-center justify-between gap-2 text-[11px] px-2.5 py-1.5 rounded-lg bg-n-solid-2 border border-n-weak"
            >
              <span class="flex items-center gap-1.5 min-w-0 text-n-slate-11">
                <span class="w-1.5 h-1.5 rounded-full flex-shrink-0" :class="bot.active ? 'bg-green-500' : 'bg-n-slate-7'" />
                <span class="truncate">{{ bot.name }}</span>
              </span>
              <button
                class="font-semibold flex-shrink-0 hover:underline disabled:opacity-50"
                :class="bot.active ? 'text-red-500' : 'text-green-600 dark:text-green-400'"
                :disabled="isTogglingFollowup"
                :title="$t('CRM.CHAT.FOLLOWUP_BOT_HINT')"
                @click="toggleFollowupBot(bot)"
              >
                {{ bot.active ? $t('CRM.CHAT.FOLLOWUP_BOT_STOP') : $t('CRM.CHAT.FOLLOWUP_BOT_START') }}
              </button>
            </div>
            <p class="text-[10px] text-n-slate-9 px-0.5">{{ $t('CRM.CHAT.FOLLOWUP_BOT_SCOPE_HINT') }}</p>
          </div>
        </div>
      </div>

      <!-- ── Sem conversa ainda: iniciar pelo WhatsApp ── -->
      <div
        v-if="!hasConversation"
        class="flex-1 flex flex-col items-center justify-center gap-4 px-6 py-8 bg-n-alpha-1"
      >
        <span class="i-lucide-message-square-plus text-4xl text-n-slate-9" />
        <div class="text-center">
          <p class="text-sm font-medium text-n-slate-12">{{ $t('CRM.CHAT.NO_CONVERSATION_TITLE') }}</p>
          <p class="text-xs text-n-slate-10 mt-1 max-w-xs">{{ $t('CRM.CHAT.NO_CONVERSATION_HINT') }}</p>
        </div>

        <div v-if="whatsappInboxes.length" class="w-full max-w-xs space-y-2">
          <select
            v-model="startInboxId"
            class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12"
          >
            <option v-for="i in whatsappInboxes" :key="i.id" :value="i.id">{{ i.name }}</option>
          </select>
          <button
            class="w-full flex items-center justify-center gap-2 bg-n-brand text-white rounded-xl py-2.5 text-sm font-medium hover:bg-n-brand/90 disabled:opacity-50 transition-colors"
            :disabled="!startInboxId || isStarting"
            @click="startConversation"
          >
            <span :class="isStarting ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-message-circle-more'" class="text-base" />
            {{ isStarting ? $t('CRM.MODAL.LOADING') : $t('CRM.CHAT.START_BUTTON') }}
          </button>
          <p class="text-[10px] text-n-slate-9 text-center">{{ $t('CRM.CHAT.START_TEMPLATE_NOTE') }}</p>
        </div>
        <p v-else class="text-xs text-n-slate-10">{{ $t('CRM.CHAT.NO_WHATSAPP_INBOX') }}</p>
      </div>

      <!-- Messages -->
      <div
        v-else
        ref="messagesEl"
        class="flex-1 overflow-y-auto px-4 py-3 space-y-2 min-h-0 bg-n-alpha-1"
        style="scrollbar-width:thin;"
      >
        <div v-if="isLoading" class="flex justify-center items-center h-full">
          <Spinner :size="28" class="text-n-brand" />
        </div>

        <template v-else>
          <div v-if="hasMore" class="flex justify-center pb-1">
            <button
              class="text-xs text-n-brand hover:underline disabled:opacity-50"
              :disabled="isLoadingMore"
              @click="loadMore"
            >
              {{ isLoadingMore ? $t('CRM.MODAL.LOADING') : $t('CRM.CHAT.LOAD_MORE') }}
            </button>
          </div>

          <div v-if="!messages.length" class="flex flex-col items-center justify-center h-full text-n-slate-10">
            <span class="i-lucide-message-square-off text-3xl mb-2" />
            <p class="text-sm">{{ $t('CRM.MODAL.NO_MESSAGES') }}</p>
          </div>

          <template v-for="m in messages" :key="m.id">
            <!-- Atividade (centralizada) -->
            <div v-if="isActivity(m)" class="flex justify-center">
              <span class="text-[11px] text-n-slate-9 bg-n-alpha-2 rounded-full px-2.5 py-0.5 text-center">
                {{ m.content }}
              </span>
            </div>

            <!-- Bolha -->
            <div v-else class="flex" :class="isIncoming(m) ? 'justify-start' : 'justify-end'">
              <!-- balão enviado: azul royal com gradiente (letra branca) -->
              <div
                class="max-w-[80%] rounded-2xl px-3 py-2"
                :class="isIncoming(m)
                  ? 'bg-n-solid-2 border border-n-weak text-n-slate-12 rounded-bl-sm'
                  : 'text-white rounded-br-sm shadow-sm'"
                :style="isIncoming(m) ? {} : { background: 'linear-gradient(135deg, #1D4ED8, #2563EB 60%, #3B82F6)' }"
              >
                <p v-if="m.content" class="text-sm whitespace-pre-wrap break-words leading-relaxed">{{ m.content }}</p>
                <div v-if="m.attachments?.length" class="space-y-1" :class="m.content ? 'mt-1.5' : ''">
                  <a
                    v-for="att in m.attachments"
                    :key="att.id"
                    :href="att.data_url"
                    target="_blank"
                    rel="noopener"
                    class="block text-xs underline"
                    :class="isIncoming(m) ? 'text-n-brand' : 'text-white/90'"
                  >
                    {{ attachmentLabel(att.file_type) }}
                  </a>
                </div>
                <p
                  class="text-[10px] mt-1 text-right"
                  :class="isIncoming(m) ? 'text-n-slate-9' : 'text-white/70'"
                >
                  {{ formatTime(m.created_at) }}
                </p>
              </div>
            </div>
          </template>
        </template>
      </div>

      <!-- ── Painel de Templates (remarketing) ── -->
      <div v-if="hasConversation && showTemplates" class="border-t border-n-weak p-3 flex-shrink-0 max-h-[55%] overflow-y-auto">
        <div class="flex items-center justify-between mb-2">
          <p class="text-sm font-semibold text-n-slate-12 flex items-center gap-1.5">
            <span class="i-lucide-message-square-text text-base text-n-brand" />
            {{ selectedTemplate ? $t('CRM.CHAT.TEMPLATE_FILL') : $t('CRM.CHAT.TEMPLATES_TITLE') }}
          </p>
          <button
            class="text-xs text-n-slate-10 hover:text-n-slate-12 flex items-center gap-1"
            @click="showTemplates = false; selectedTemplate = null"
          >
            <span class="i-lucide-x text-sm" />
            {{ $t('CRM.CANCEL') }}
          </button>
        </div>

        <WhatsAppTemplateReply
          v-if="selectedTemplate"
          :template="selectedTemplate"
          @send-message="onTemplateSend"
          @reset-template="selectedTemplate = null"
        />
        <TemplatesPicker
          v-else
          :inbox-id="inboxId"
          @on-select="onTemplateSelect"
        />
      </div>

      <!-- Reply box -->
      <div v-else-if="hasConversation" class="border-t border-n-weak p-3 flex-shrink-0">
        <!-- Ações: resolver + templates -->
        <div class="flex items-center gap-2 mb-2">
          <button
            class="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border transition-colors"
            :class="isResolved
              ? 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
              : 'border-green-500/40 text-green-600 hover:bg-green-500/10'"
            :disabled="isResolving"
            @click="toggleResolve"
          >
            <span
              :class="isResolving ? 'i-lucide-loader-2 animate-spin' : (isResolved ? 'i-lucide-rotate-ccw' : 'i-lucide-check-check')"
              class="text-sm"
            />
            {{ isResolved ? $t('CRM.CHAT.REOPEN') : $t('CRM.CHAT.RESOLVE') }}
          </button>

          <button
            v-if="isWhatsapp"
            class="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
            @click="openTemplates"
          >
            <span class="i-lucide-message-square-text text-sm" />
            {{ $t('CRM.CHAT.TEMPLATES') }}
          </button>
        </div>

        <div class="flex items-end gap-2 relative">
          <CannedResponse
            v-if="showCannedMenu"
            :search-key="cannedSearchKey"
            @replace="replaceTextWithCanned"
          />
          <textarea
            v-model="replyText"
            rows="2"
            class="flex-1 border border-n-weak rounded-xl px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 resize-none focus:outline-none focus:border-n-brand"
            :placeholder="$t('CRM.CHAT.REPLY_PLACEHOLDER')"
            @keydown="onKeydown"
          />
          <!-- Emoji | Enviar (alinhados juntos, à direita) -->
          <div class="flex items-center gap-1.5 flex-shrink-0">
            <div ref="emojiWrap" class="relative">
              <button
                class="flex items-center justify-center w-10 h-10 rounded-xl border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
                :class="showEmoji ? 'border-n-brand text-n-brand' : ''"
                :title="$t('CRM.CHAT.EMOJI')"
                @click="showEmoji = !showEmoji"
              >
                <span class="i-lucide-smile text-base" />
              </button>
              <div v-if="showEmoji" class="absolute bottom-12 right-0 z-20">
                <EmojiPicker @select="onInsertEmoji" />
              </div>
            </div>
            <button
              class="flex items-center justify-center w-10 h-10 rounded-xl bg-n-brand text-white hover:bg-n-brand/90 transition-colors disabled:opacity-50"
              :disabled="!replyText.trim() || isSending"
              :title="$t('CRM.CHAT.SEND')"
              @click="sendReply"
            >
              <span :class="isSending ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-send'" class="text-base" />
            </button>
          </div>
        </div>
        <p class="text-[10px] text-n-slate-9 mt-1.5">
          {{ $t('CRM.CHAT.OFFICIAL_NOTE', { inbox: contact.last_conversation?.inbox_name ?? '' }) }}
        </p>
      </div>
    </div>
  </div>
</template>
